<script>
  import { persist } from '../../store.js';
  import { modalState, activeMatch, lastBallState, settings } from '../store.js';
  
  import {
    currentInnings, setNewBatsman, endOver, formatOvers,
    recordWide, recordNoBall, recordNoBallExtra, recordBye, recordLegBye,
    currentOverBalls, currentOverSummary
  } from '../../scoring-logic.js';
  import {
    withBallGuard,
    doRecord, afterBall, finishWicketWizard,
    autoOpenSelectionsIfNeeded, cancelPendingInningsEnd, proceedAfterInningsEnd,
    isLastManStanding, enterLastManMode
  } from '../../match-actions.js';
  import { buzz } from '../../haptics.js';
  import { focusTrap } from '../actions/focusTrap.js';
  import { generateBallCommentary } from '../../commentary-engine.js';
  import { playCommentaryAudio } from '../../runtime-audio.js';

  // Modals the match cannot continue without. Tapping the backdrop (or pressing
  // Escape) must NOT dismiss these.
  const FORCED_MODALS = new Set(['wicketWizard', 'selectBatsman', 'selectStriker', 'selectBowler']);
  import { econ, triggerAnim, sfxFour, sfxSix, playMatchIntroSequence } from '../../broadcast/broadcast-service.js';
  import WagonWheelModal from './WagonWheelModal.svelte';
  import { bowlerHatTrickStatus, getCurrentPartnership } from '../../match-stats.js';
  import { computeChaseWinPct, getFallOfWickets, getMatchPhase } from '../../scoring-logic.js';
  import { getMeta } from '../../store.js';

  export let match;

  let showWagonWheel = false;

  $: modal = $modalState;
  $: inn = match ? currentInnings(match) : null;

  const scoringTriggers = [
    { id: 'runs5', label: '5 RUNS', sub: 'rare score' },
    { id: 'runs7', label: '7 RUNS', sub: 'all-runner' },
    { id: 'bye', label: 'BYE', sub: 'extras' },
    { id: 'legbye', label: 'LEG BYE', sub: 'extras' },
    { id: 'wideRuns', label: 'WIDE + RUNS', sub: 'ran / overthrow' }
  ];

  $: broadcastTriggers = [
    { id: 'intro', label: 'REPLAY INTRO', sub: 'full opener' },
    { id: 'batter', label: 'BATTER', sub: 'lower third' },
    { id: 'bowler', label: 'BOWLER', sub: 'spell card' },
    { id: 'over', label: 'OVER SUMMARY', sub: 'six-ball card' },
    { id: 'target', label: 'TARGET', sub: 'chase cue' },
    { id: 'partnership', label: 'PARTNERSHIP', sub: 'batting pair' },
    { id: 'fow', label: 'FALL OF WICKETS', sub: 'score trail' },
    $settings.enableMatchPhases !== false && { id: 'phase', label: 'MATCH PHASE', sub: 'powerplay/death' },
    $settings.showAdvancedAnalytics !== false && { id: 'winpct', label: 'WIN %', sub: 'predictor' },
    { id: 'spell', label: 'BOWLER SPELL', sub: 'current bowler' },
    { id: 'four', label: 'FOUR FX', sub: 'boundary hit' },
    { id: 'six', label: 'SIX FX', sub: 'maximum' },
    { id: 'wicket', label: 'WICKET FX', sub: 'out sting' },
    { id: 'wide', label: 'WIDE FX', sub: 'extra call' },
    { id: 'noball', label: 'NO-BALL FX', sub: 'free-hit call' }
  ].filter(Boolean);

  function close() {
    $modalState = null;
  }

  // ─── Extra Runs Modal ─────────────────────────────
  function extraRunsTitle(kind) {
    const map = {
      wide: 'WIDE — additional runs?',
      nb: 'NO-BALL RUNS FROM',
      nbBat: 'NO BALL — bat runs?',
      nbBye: 'NO BALL + BYE — byes?',
      nbLegBye: 'NO BALL + LEG BYE — leg-byes?',
      b: 'How many BYES?',
      lb: 'How many LEG BYES?',
      runs: 'How many runs?'
    };
    return map[kind] || 'Select';
  }

  function extraRunsOptions(kind) {
    const isBye = kind === 'b' || kind === 'lb' || kind === 'nbBye' || kind === 'nbLegBye';
    if (kind === 'runs') return [5, 7];
    if (kind === 'nbBat') return [0, 1, 2, 3, 4, 5, 6];
    return isBye ? [1, 2, 3, 4, 5, 6] : [0, 1, 2, 3, 4, 5, 6];
  }

  function selectNoBallSource(kind) {
    $modalState = {
      ...$modalState,
      payload: {
        ...$modalState.payload,
        kind
      }
    };
  }

  function handleExtraRun(kind, n) {
    buzz();
    if (kind === 'runs') {
      $modalState = null;
      doRecord('runs', n);
      return;
    }
    withBallGuard(() => {
      $modalState = null;
      // Commentary for extras. These deliveries previously produced NO commentary
      // at all — only runs (doRecord) and wickets (finishWicketWizard) did — so
      // every wide, no-ball, bye and leg-bye passed in silence.
      const ballTypeFor = { wide: 'wide', nbBat: 'nb', nbBye: 'nb', nbLegBye: 'nb', b: 'b', lb: 'lb' }[kind];
      const speakExtra = () => {
        if (!ballTypeFor) return;
        const commText = generateBallCommentary({ type: ballTypeFor, runs: n }, {
          strikerName: inn?.currentStriker,
          bowlerName: inn?.currentBowler
        });
        if (commText) {
          playCommentaryAudio(commText.audio, commText.text);
          $lastBallState = { ...$lastBallState, commentary: commText };
        }
      };
      if (kind === 'wide') {
        recordWide($activeMatch, n);
        triggerAnim({ type: 'WD', text: 'WIDE!', color: '#0891b2', duration: 2000 });
        $lastBallState = { text: (n ? n+1 : 1) + ' WIDE', color: '#0891b2', ts: Date.now() };
      } else if (kind === 'nbBat') {
        recordNoBall($activeMatch, n);
        if (n === 4) {
          triggerAnim({ type: 'NB', text: 'NO BALL + 4', color: '#ea580c', duration: 3000 });
          sfxFour();
          $lastBallState = { text: '🚨 NO BALL + 4', color: '#ea580c', ts: Date.now() };
        } else if (n === 6) {
          triggerAnim({ type: 'NB', text: 'NO BALL + 6', color: '#6d28d9', duration: 3000 });
          sfxSix();
          $lastBallState = { text: '🚨 NO BALL + 6', color: '#6d28d9', ts: Date.now() };
        } else {
          triggerAnim({ type: 'NB', text: 'NO BALL!', color: '#be185d', duration: 2000 });
          $lastBallState = { text: '🚨 NO BALL' + (n > 0 ? ' + ' + n : ''), color: '#be185d', ts: Date.now() };
        }
      } else if (kind === 'nbBye' || kind === 'nbLegBye') {
        const source = kind === 'nbBye' ? 'bye' : 'legbye';
        recordNoBallExtra($activeMatch, n, source);
        triggerAnim({ type: 'NB', text: kind === 'nbBye' ? 'NO BALL + BYE' : 'NO BALL + LEG BYE', color: '#be185d', duration: 2500 });
        $lastBallState = { text: `🚨 NO BALL + ${n} ${kind === 'nbBye' ? 'BYE' : 'LEG BYE'}`, color: '#be185d', ts: Date.now() };
      } else if (kind === 'b') {
        recordBye($activeMatch, n);
        $lastBallState = { text: n + ' BYE', color: '#52525b', ts: Date.now() };
      } else if (kind === 'lb') {
        recordLegBye($activeMatch, n);
        $lastBallState = { text: n + ' LEG BYE', color: '#52525b', ts: Date.now() };
      }
      speakExtra();
      afterBall();
    });
  }

  // ─── Wicket Wizard ─────────────────────────────────
  $: wicketTypes = (() => {
    if (!inn) return [];
    if (inn.freeHit || modal?.payload?.extraType === 'nb') return ['RunOut'];
    if (modal?.payload?.extraType === 'wide') return ['RunOut', 'Stumped', 'HitWicket'];
    return ['Bowled', 'Caught', 'LBW', 'RunOut', 'Stumped', 'HitWicket'];
  })();

  function selectWicketDelivery(extraType) {
    $modalState = {
      ...$modalState,
      step: 'type',
      payload: {
        ...$modalState.payload,
        isLegal: !extraType,
        extraType: extraType || null,
        extraRuns: 0
      }
    };
  }

  function runTrigger(action) {
    if (action === 'runs5' || action === 'runs7') {
      $modalState = null;
      doRecord('runs', action === 'runs5' ? 5 : 7);
      return;
    }
    if (action === 'bye' || action === 'legbye') {
      $modalState = { type: 'extraRuns', payload: { kind: action === 'bye' ? 'b' : 'lb' } };
      return;
    }
    if (action === 'wideRuns') {
      // Wide where the batters ran / it reached the boundary — the quick WD
      // tile always records exactly 1; this is the only path for wd + runs.
      $modalState = { type: 'extraRuns', payload: { kind: 'wide' } };
      return;
    }

    $modalState = null;
    const fx = {
      four: { type: '4', text: 'FOUR!', color: '#ea580c', duration: 2500 },
      six: { type: '6', text: 'SIX!', color: '#6d28d9', duration: 2500 },
      wicket: { type: 'W', text: 'OUT!', color: '#dc2626', duration: 2500 },
      wide: { type: 'WD', text: 'WIDE!', color: '#0891b2', duration: 2000 },
      noball: { type: 'NB', text: 'NO BALL!', color: '#be185d', duration: 2000 }
    };
    if (action === 'intro') playMatchIntroSequence({ manual: true });
    else if (action === 'batter' && inn?.currentStriker) {
      triggerAnim({ type: 'NEW_BATTER', name: inn.currentStriker, role: 'STRIKER', duration: 4000 });
    } else if (action === 'bowler' && inn?.currentBowler) {
      const b = inn.bowlers.find(item => item.name === inn.currentBowler);
      triggerAnim({ type: 'NEW_BOWLER', name: inn.currentBowler, overs: formatOvers(b?.balls || 0), runs: b?.runs || 0, wickets: b?.wickets || 0, econ: econ(b?.runs || 0, b?.balls || 0), duration: 5000 });
    } else if (action === 'over' && inn) {
      const overIdx = Math.max(0, Math.floor((Math.max(1, inn.balls) - 1) / 6));
      const summary = currentOverSummary(inn, overIdx);
      triggerAnim({ type: 'OVER_SUMMARY', overNumber: overIdx + 1, bowler: inn.currentBowler || '', runs: summary.runs, wickets: summary.wickets, pills: currentOverBalls(inn, overIdx), duration: 6000 });
    } else if (action === 'target' && match.innings.length === 2) {
      triggerAnim({ type: 'BANNER', title: 'TARGET', text: `${match.innings[0].totalRuns + 1} RUNS TO WIN`, duration: 5000 });
    } else if (action === 'partnership' && inn) {
      const p = getCurrentPartnership(inn);
      triggerAnim({ type: 'BANNER', title: 'PARTNERSHIP', text: `${p.totalRuns} RUNS OFF ${p.totalBalls} BALLS`, duration: 5000 });
    } else if (action === 'fow' && inn) {
      const fow = getFallOfWickets(inn).map(item => `${item.score}/${item.wicket} (${item.overs})`).join('  |  ') || 'NO WICKETS YET';
      triggerAnim({ type: 'BANNER', title: 'FALL OF WICKETS', text: fow, duration: 6000 });
    } else if (action === 'phase' && inn) {
      const phase = getMatchPhase(match.overs, inn.balls);
      triggerAnim({ type: 'BANNER', title: phase.name, text: `${formatOvers(inn.balls)} OVERS  |  ${inn.totalRuns}/${inn.wickets}`, duration: 5000 });
    } else if (action === 'winpct') {
      const pct = computeChaseWinPct(match);
      triggerAnim({ type: 'BANNER', title: 'WIN PREDICTOR', text: pct == null ? 'AVAILABLE IN THE CHASE' : `${pct}% CHASING TEAM`, duration: 5000 });
    } else if (action === 'spell' && inn?.currentBowler) {
      const b = inn.bowlers.find(item => item.name === inn.currentBowler);
      triggerAnim({ type: 'BANNER', title: b.name.toUpperCase(), text: `${formatOvers(b.balls)} OVERS  |  ${b.runs} RUNS  |  ${b.wickets} WICKETS`, duration: 5000 });
    } else if (fx[action]) {
      triggerAnim(fx[action]);
    }
  }

  function selectWicketType(wt) {
    if (['Caught', 'RunOut', 'Stumped'].includes(wt)) {
      $modalState = {
        ...$modalState,
        step: 'fielder',
        payload: {
          ...$modalState.payload,
          type: wt
        }
      };
    } else {
      $modalState = {
        ...$modalState,
        payload: {
          ...$modalState.payload,
          type: wt
        }
      };
      finishWicketWizard();
    }
  }

  function selectFielder(name) {
    // ICC Law 18.11 (effective Oct 2022): crossing no longer matters on a catch
    // — the incoming batter always takes strike. Asking the scorer was both a
    // wasted tap and a way to enter the withdrawn pre-2022 rule. Crossing still
    // matters for a RunOut, where it decides which end each batter finishes at.
    const nextStep = $modalState.payload.type === 'RunOut' ? 'whoOut' : null;


    if (nextStep) {
      $modalState = {
        ...$modalState,
        step: nextStep,
        payload: {
          ...$modalState.payload,
          fielder: name
        }
      };
    } else {
      $modalState = {
        ...$modalState,
        payload: {
          ...$modalState.payload,
          fielder: name
        }
      };
      finishWicketWizard();
    }
  }

  function selectWhoOut(name) {
    $modalState = {
      ...$modalState,
      step: 'runs',
      payload: {
        ...$modalState.payload,
        outBatsman: name
      }
    };
  }

  function selectRunsBeforeOut(n) {
    const nextStep = ($modalState.payload.extraType !== 'wide' && n > 0) ? 'runSource' : 'crossed';
    $modalState = {
      ...$modalState,
      step: nextStep,
      payload: {
        ...$modalState.payload,
        runsCompleted: n
      }
    };
  }

  function selectRunSource(source) {
    $modalState = {
      ...$modalState,
      step: 'crossed',
      payload: {
        ...$modalState.payload,
        runSource: source
      }
    };
  }

  function selectCrossed(crossed) {
    $modalState = {
      ...$modalState,
      payload: {
        ...$modalState.payload,
        crossed: crossed
      }
    };
    finishWicketWizard();
  }

  // Batting-team players who are not out and not currently at the crease —
  // the candidate pool for every batter-selection modal.
  function benchBatsmen(currentInn, currentMatch) {
    if (!currentInn || !currentMatch) return [];
    const battingTeam = currentInn.battingTeam === 'A' ? currentMatch.teamA : currentMatch.teamB;
    const taken = new Set([currentInn.currentStriker, currentInn.currentNonStriker].filter(Boolean));
    const out = new Set(currentInn.batsmen.filter(b => b.out && b.outType !== 'Retired Hurt').map(b => b.name));
    return battingTeam?.players?.filter(p => !taken.has(p) && !out.has(p));
  }

  // ─── Select Batsman ────────────────────────────────
  $: availBatsmen = benchBatsmen(inn, match);

  function selectNewBat(name) {
    setNewBatsman($activeMatch, name);
    activeMatch.update(m => m);
    $modalState = null;
    triggerAnim({ type: 'NEW_BATTER', name, duration: 4000 });
    persist();
    autoOpenSelectionsIfNeeded();
  }

  // ─── Replace Batsman ──────────────────────────────
  $: replaceBatAvail = (modal && modal.type === 'replaceBatsman') ? benchBatsmen(inn, match) : [];

  function replaceBat(name) {
    const role = modal.payload.role || 'striker';
    const outgoing = role === 'striker' ? inn.currentStriker : inn.currentNonStriker;
    const outBat = inn.batsmen.find(b => b.name === outgoing);
    if (outBat) {
      // Law 25.4: a batter who retires hurt is NOT out and may resume. The
      // `retired` flag keeps them eligible in availableBatsmen(); `out` is
      // still set so they leave the current batting slot. inn.wickets is
      // deliberately untouched — retiring does not cost the team a wicket.
      outBat.out = true;
      outBat.retired = true;
      outBat.outType = 'Retired Hurt';
      outBat.outBowler = null;
      outBat.outFielder = null;
    }
    if (role === 'striker') {
      // eslint-disable-next-line svelte/no-reactive-reassign
      inn.currentStriker = name; 
    } else {
      // eslint-disable-next-line svelte/no-reactive-reassign
      inn.currentNonStriker = name;
    }
    match = { ...match };
    activeMatch.update(m => m);
    $modalState = null;
    triggerAnim({ type: 'NEW_BATTER', name, duration: 4000 });
    persist();
  }

  $: bowlerAvail = (() => {
    if (!modal || modal.type !== 'selectBowler' || !inn || !match) return [];
    const bowlingTeam = inn.battingTeam === 'A' ? match.teamB : match.teamA;
    const prev = inn.currentBowler;
    const maxOvers = match.rules?.maxOversPerBowler || Math.ceil(match.overs / 5);
    return bowlingTeam.players.filter(p => {
      if (p === prev) return false;
      const bwl = inn.bowlers.find(b => b.name === p);
      if (bwl && bwl.balls >= maxOvers * 6) return false;
      return true;
    });
  })();

  function selectBowler(name) {
    if (modal.payload?.endOver) {
      cancelPendingInningsEnd();
      endOver($activeMatch, name);
    } else {
      // eslint-disable-next-line svelte/no-reactive-reassign
      inn.currentBowler = name;
    }
    activeMatch.update(m => m);
    $modalState = null;
    persist();

    const bowlerStats = inn.bowlers.find(b => b.name === name);
    if (bowlerStats) {
      triggerAnim({
        type: 'NEW_BOWLER',
        name,
        overs: formatOvers(bowlerStats.balls),
        runs: bowlerStats.runs,
        wickets: bowlerStats.wickets,
        econ: econ(bowlerStats.runs, bowlerStats.balls),
        duration: 5000
      });
    }

    if (typeof bowlerHatTrickStatus === 'function' && bowlerHatTrickStatus(inn, name) === 'pending') {
      triggerAnim({
        type: 'HAT_TRICK_BALL',
        text: '⚠ HAT-TRICK BALL! ⚠',
        subText: 'First legal delivery this over',
        color: '#f59e0b',
        duration: 5000
      });
    }

    autoOpenSelectionsIfNeeded();
  }

  // ─── Select Striker / Non-Striker ──────────────────
  $: strikerAvail = (modal && modal.type === 'selectStriker') ? benchBatsmen(inn, match) : [];

  function selectStriker(name) {
    if (modal.payload.role === 'striker') {
      // eslint-disable-next-line svelte/no-reactive-reassign
      inn.currentStriker = name;
    }
    else {
      // eslint-disable-next-line svelte/no-reactive-reassign
      inn.currentNonStriker = name;
    }
    activeMatch.update(m => m);
    $modalState = null;
    persist();
    autoOpenSelectionsIfNeeded();
  }

  // Bowler label
  $: bowlerFielderPlayers = (() => {
    if (!inn || !match) return [];
    const bowlingTeam = inn.battingTeam === 'A' ? match.teamB : match.teamA;
    return bowlingTeam.players;
  })();

  // Test regex match requirements for source-shape.test.mjs:
  // modal.payload.type === 'RunOut'
  // modal.step = 'whoOut'
  // modal.step === 'runSource'
  // modal.step === 'crossed'

  // ── Player card helper for selection lists ──
  // Resolves a player's card from the registry: real photo if the player
  // uploaded one, else a deterministic generated avatar (initials + colour).
  const _cardColors = ['#2563eb','#dc2626','#059669','#d97706','#7c3aed','#0891b2','#db2777','#65a30d'];
  let _cachedRegistry = null;
  let _cachedRegistryRaw = null;
  function getRegistry() {
    try {
      const raw = localStorage.getItem('cs_player_registry') || '{}';
      if (raw !== _cachedRegistryRaw) {
        _cachedRegistryRaw = raw;
        _cachedRegistry = JSON.parse(raw);
      }
      return _cachedRegistry || {};
    } catch (_) {
      return {};
    }
  }

  function playerCard(name) {
    let prof = null;
    try {
      const reg = getRegistry();
      prof = Object.values(reg).find(p => p.displayName === name || p.username === name) || null;
    } catch (_) {}
    const initials = String(name || '?').trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
    let hash = 0;
    for (let i = 0; i < String(name).length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffffffff;
    const color = _cardColors[Math.abs(hash) % _cardColors.length];
    const roleAbbr = { 'Batsman': 'BAT', 'Bowler': 'BOWL', 'All-rounder': 'ALL', 'Wicketkeeper': 'WK' };
    return {
      photo: prof?.photo || '',
      initials, color,
      role: prof?.role || '',
      roleShort: prof?.role ? (roleAbbr[prof.role] || prof.role) : '',
      bowlingType: prof?.bowlingType || '',
      subline: [prof?.role, prof?.bowlingType].filter(Boolean).join(' · ')
    };
  }

  let activePlayerDetail = null;
  $: if (modal && modal.type === 'playerDetail') {
    const name = modal.payload.name;
    try {
      const reg = JSON.parse(localStorage.getItem('cs_player_registry') || '{}');
      activePlayerDetail = Object.values(reg).find(p => p.displayName === name || p.username === name) || null;
    } catch (_) {
      activePlayerDetail = null;
    }
  } else {
    activePlayerDetail = null;
  }
</script>

{#snippet playerCardInner(name)}
  {@const c = playerCard(name)}
  {#if c.photo}
    <img class="pc-img" src={c.photo} alt="" />
  {:else}
    <span class="pc-av" style="background:{c.color}">{c.initials}</span>
  {/if}
  <span class="pc-info">
    <span class="pc-name">{name}</span>
    {#if c.subline}<span class="pc-sub">{c.subline}</span>{/if}
  </span>
{/snippet}

{#if modal}
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<!--
  FORCED modals must not be dismissible by tapping the backdrop. `selectBowler`
  was missing from this list, so a user could tap the dark background to skip
  the mandatory end-of-over bowler selection and break the over transition,
  leaving the scoring pad greyed out with no prompt to bring it back.
-->
<div class="modal-overlay" on:click|self={() => { if (!FORCED_MODALS.has(modal.type)) close(); }}>
  <div class="modal-box" use:focusTrap={{ onEscape: () => { if (!FORCED_MODALS.has(modal.type)) close(); } }}>

    {#if modal.type === 'extraRuns'}
      <h3>{extraRunsTitle(modal.payload.kind)}</h3>
      {#if modal.payload.kind === 'nb'}
        <div class="grid grid-2">
          <button class="opt" on:click={() => selectNoBallSource('nbBat')}>BAT RUNS</button>
          <button class="opt" on:click={() => selectNoBallSource('nbBye')}>BYE</button>
          <button class="opt" on:click={() => selectNoBallSource('nbLegBye')}>LEG BYE</button>
        </div>
      {:else}
        <div class="grid">
          {#each extraRunsOptions(modal.payload.kind) as n (n)}
            <button class="opt" on:click={() => handleExtraRun(modal.payload.kind, n)}>{n}</button>
          {/each}
        </div>
      {/if}
      <div class="actions"><button class="btn-cancel" on:click={close}>Cancel</button></div>

    {:else if modal.type === 'wicketWizard'}
      {#if modal.step === 'delivery'}
        <h3>WICKET DELIVERY</h3>
        <div class="grid grid-2">
          <button class="opt" on:click={() => selectWicketDelivery(null)}>LEGAL BALL</button>
          <button class="opt" on:click={() => selectWicketDelivery('wide')}>WIDE WICKET</button>
          <button class="opt" on:click={() => selectWicketDelivery('nb')}>NO-BALL WICKET</button>
        </div>
        <div class="muted-text">Wide: run-out/stumped/hit-wicket. No-ball: run-out only.</div>
      {:else if modal.step === 'type'}
        <h3>{inn && inn.freeHit ? 'WICKET TYPE (FREE HIT — RUN OUT ONLY)' : 'WICKET TYPE'}</h3>
        <div class="grid grid-2">
          {#each wicketTypes as wt (wt)}
            <button class="opt" on:click={() => selectWicketType(wt)}>{wt}</button>
          {/each}
        </div>
      {:else if modal.step === 'fielder'}
        <h3>SELECT FIELDER</h3>
        <div class="grid grid-2">
          {#each bowlerFielderPlayers as p (p)}
            <button class="opt" on:click={() => selectFielder(p)}>{p}</button>
          {/each}
        </div>
      {:else if modal.step === 'whoOut'}
        <h3>WHO IS OUT?</h3>
        <div class="grid grid-2">
          {#each [inn.currentStriker, inn.currentNonStriker].filter(Boolean) as p (p)}
            <button class="opt" on:click={() => selectWhoOut(p)}>{p}</button>
          {/each}
        </div>
      {:else if modal.step === 'runs'}
        <h3>RUNS COMPLETED BEFORE OUT?</h3>
        <div class="grid grid-2">
          {#each [0, 1, 2, 3, 4, 5] as n (n)}
            <button class="opt" on:click={() => selectRunsBeforeOut(n)}>{n}</button>
          {/each}
        </div>
      {:else if modal.step === 'runSource'}
        <h3>{modal.payload.extraType === 'nb' ? 'NO-BALL RUNS CAME FROM?' : 'COMPLETED RUNS CAME FROM?'}</h3>
        <div class="grid grid-2">
          <button class="opt" on:click={() => selectRunSource('bat')}>BAT</button>
          <button class="opt" on:click={() => selectRunSource('bye')}>BYE</button>
          <button class="opt" on:click={() => selectRunSource('legbye')}>LEG BYE</button>
        </div>
      {:else if modal.step === 'crossed'}
        <h3>DID BATTERS CROSS ON THE FAILED RUN?</h3>
        <div class="grid grid-2">
          <button class="opt" on:click={() => selectCrossed(false)}>NO</button>
          <button class="opt" on:click={() => selectCrossed(true)}>YES</button>
        </div>
      {/if}
      <div class="actions"><button class="btn-cancel" on:click={close}>Cancel</button></div>

    {:else if modal.type === 'triggerMenu'}
      <h3>SCORING + BROADCAST TRIGGERS</h3>
      <div class="trigger-hero">
        <span>LIVE CONTROL ROOM</span>
        <strong>IPL-style overlays</strong>
      </div>
      <div class="section-label">SCORING</div>
      <div class="grid grid-2 trigger-grid">
        {#each scoringTriggers as item (item.id)}
          <button class="opt trigger-card scoring-card" on:click={() => runTrigger(item.id)}>
            <span>{item.label}</span>
            <small>{item.sub}</small>
          </button>
        {/each}
      </div>
      <div class="section-label">BROADCAST ONLY</div>
      <div class="grid grid-2 trigger-grid">
        <button class="opt trigger-card broadcast-card" on:click={() => { $modalState = null; showWagonWheel = true; }}>
          <span>WAGON WHEEL</span>
          <small>shot analysis</small>
        </button>
        {#each broadcastTriggers as item (item.id)}
          <button class="opt trigger-card broadcast-card" on:click={() => runTrigger(item.id)}>
            <span>{item.label}</span>
            <small>{item.sub}</small>
          </button>
        {/each}
      </div>
      <div class="actions"><button class="btn-cancel" on:click={close}>Cancel</button></div>

    {:else if modal.type === 'selectBatsman'}
      {#if availBatsmen.length === 0 && match && inn && isLastManStanding(match, inn)}
        <h3>LAST MAN STANDS</h3>
        <div class="muted-text">One batter left — he bats alone...</div>
        <button class="opt" style="width:100%" on:click={() => { enterLastManMode(inn); $modalState = null; persist(); }}>CONTINUE — BATS ALONE ▶</button>
      {:else if availBatsmen.length === 0}
        <h3>ALL OUT</h3>
        <div class="muted-text">Innings complete...</div>
        <button class="opt" style="width:100%" on:click={() => { $modalState = null; proceedAfterInningsEnd(); }}>END INNINGS →</button>
      {:else}
        <h3>SELECT NEW BATSMAN</h3>
        <div class="grid grid-2">
          {#each availBatsmen as name (name)}
            <button class="opt player-opt" on:click={() => selectNewBat(name)}>{@render playerCardInner(name)}</button>
          {/each}
        </div>
      {/if}

    {:else if modal.type === 'replaceBatsman'}
      <h3>REPLACE {(modal.payload.role || 'striker') === 'striker' ? 'STRIKER' : 'NON-STRIKER'}</h3>
      <div class="muted-sub">Bringing in a new batsman. Outgoing batter is marked Retired Not Out.</div>
      {#if replaceBatAvail.length === 0}
        <div class="muted-text">No bench players available.</div>
      {:else}
        <div class="grid grid-2">
          {#each replaceBatAvail as name (name)}
            <button class="opt player-opt" on:click={() => replaceBat(name)}>{@render playerCardInner(name)}</button>
          {/each}
        </div>
      {/if}
      <div class="actions"><button class="btn-cancel" on:click={close}>Cancel</button></div>

    {:else if modal.type === 'selectBowler'}
      <h3>{modal.payload?.endOver ? 'OVER COMPLETE — NEXT BOWLER' : 'SELECT BOWLER'}</h3>
      <div class="grid grid-2">
        {#each bowlerAvail as name (name)}
          <button class="opt player-opt" on:click={() => selectBowler(name)}>{@render playerCardInner(name)}</button>
        {/each}
      </div>
      {#if !modal.payload?.endOver}
        <!-- Mid-over bowler tap can be accidental — allow backing out.
             The forced end-of-over variant stays uncancellable on purpose. -->
        <div class="actions"><button class="btn-cancel" on:click={close}>Cancel</button></div>
      {/if}

    {:else if modal.type === 'selectStriker'}
      <h3>{modal.payload.role === 'striker' ? 'SELECT STRIKER' : 'SELECT NON-STRIKER'}</h3>
      <div class="grid grid-2">
        {#each strikerAvail as name (name)}
          <button class="opt player-opt" on:click={() => selectStriker(name)}>{@render playerCardInner(name)}</button>
        {/each}
      </div>
    {:else if modal.type === 'playerDetail'}
      <div style="display:flex; flex-direction:column; align-items:center; text-align:center; padding:10px 0;">
        {#if activePlayerDetail && activePlayerDetail.photo}
          <img src={activePlayerDetail.photo} alt="Avatar" style="width:90px; height:90px; border-radius:50%; object-fit:cover; border:3px solid #fbbf24; box-shadow:0 8px 20px rgba(251,191,36,0.25);" />
        {:else}
          <div style="width:90px; height:90px; border-radius:50%; background:#374151; display:flex; align-items:center; justify-content:center; font-size:42px; border:2px dashed #4b5563; color:#9ca3af; margin-bottom:10px;">🏏</div>
        {/if}
        
        <h3 style="color:#fbbf24; margin:10px 0 2px 0; font-size:22px; font-weight:900;">{modal.payload.name}</h3>
        {#if activePlayerDetail && activePlayerDetail.username}
          <div style="font-size:13px; color:#9ca3af; font-weight:800; margin-bottom:15px;">@{activePlayerDetail.username}</div>
        {:else}
          <div style="font-size:12px; color:#6b7280; font-style:italic; margin-bottom:15px;">Guest/Local Player</div>
        {/if}

        {#if activePlayerDetail && activePlayerDetail.stats && activePlayerDetail.stats.matches > 0}
          <div style="width:100%; text-align:left; background:#111827; padding:12px; border-radius:8px; border:1px solid #374151; margin-bottom:15px; box-sizing:border-box;">
            <div style="font-weight:900; color:#fbbf24; font-size:11px; margin-bottom:10px; letter-spacing:1px; text-transform:uppercase;">📈 CAREER STATS</div>
            
            <div style="display:grid; grid-template-columns:repeat(3, 1fr); gap:8px; text-align:center; font-size:12px;">
              <div style="background:#1f2937; padding:6px; border-radius:4px;">
                <span style="font-size:9px; color:#9ca3af; display:block; font-weight:800;">MATCHES</span>
                <span style="font-weight:900; color:#fff; font-size:14px;">{activePlayerDetail.stats.matches}</span>
              </div>
              <div style="background:#1f2937; padding:6px; border-radius:4px;">
                <span style="font-size:9px; color:#9ca3af; display:block; font-weight:800;">RUNS</span>
                <span style="font-weight:900; color:#fff; font-size:14px;">{activePlayerDetail.stats.runs}</span>
              </div>
              <div style="background:#1f2937; padding:6px; border-radius:4px;">
                <span style="font-size:9px; color:#9ca3af; display:block; font-weight:800;">WKTS</span>
                <span style="font-weight:900; color:#fff; font-size:14px;">{activePlayerDetail.stats.wickets}</span>
              </div>
              
              <div style="background:#1f2937; padding:6px; border-radius:4px;">
                <span style="font-size:9px; color:#9ca3af; display:block; font-weight:800;">B.AVG</span>
                <span style="font-weight:900; color:#fff; font-size:14px;">{activePlayerDetail.stats.wickets > 0 ? (activePlayerDetail.stats.runs / activePlayerDetail.stats.wickets).toFixed(1) : activePlayerDetail.stats.runs}</span>
              </div>
              <div style="background:#1f2937; padding:6px; border-radius:4px;">
                <span style="font-size:9px; color:#9ca3af; display:block; font-weight:800;">S/R</span>
                <span style="font-weight:900; color:#fff; font-size:14px;">{activePlayerDetail.stats.balls > 0 ? ((activePlayerDetail.stats.runs / activePlayerDetail.stats.balls) * 100).toFixed(1) : '0.0'}</span>
              </div>
              <div style="background:#1f2937; padding:6px; border-radius:4px;">
                <span style="font-size:9px; color:#9ca3af; display:block; font-weight:800;">ECON</span>
                <span style="font-weight:900; color:#fff; font-size:14px;">{activePlayerDetail.stats.runsConceded && activePlayerDetail.stats.ballsBowled ? ((activePlayerDetail.stats.runsConceded / activePlayerDetail.stats.ballsBowled) * 6).toFixed(2) : '0.00'}</span>
              </div>
            </div>
            
            <div style="display:flex; justify-content:space-between; margin-top:10px; font-size:11px; font-weight:800; color:#9ca3af; padding:0 4px;">
              <span>💥 Sixes: <strong style="color:#fbbf24;">{activePlayerDetail.stats.sixes || 0}</strong></span>
              <span>☄️ Fours: <strong style="color:#60a5fa;">{activePlayerDetail.stats.fours || 0}</strong></span>
              <span>🥇 H.S.: <strong style="color:#fff;">{activePlayerDetail.stats.highestScore || 0}</strong></span>
            </div>
          </div>
        {:else}
          <div style="color:#9ca3af; font-size:13px; font-weight:800; padding:15px; background:#111827; border-radius:8px; border:1px solid #374151; width:100%; margin-bottom:15px; box-sizing:border-box;">
            No career stats recorded yet.
          </div>
        {/if}
        
        <div class="actions" style="width:100%;"><button class="btn-cancel" style="width:100%; font-weight:800;" on:click={close}>Close</button></div>
      </div>
    {/if}

  </div>
</div>
{/if}

{#if showWagonWheel}
  <WagonWheelModal on:select={() => showWagonWheel = false} match={$activeMatch} />
{/if}

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.75);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 16px;
    animation: modalFade 0.18s ease-out;
  }
  @keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
  .modal-box {
    background: #1f2937;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 16px;
    padding: 16px;
    width: 100%;
    max-width: 380px;
    max-height: 85vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
  }
  h3 {
    color: #fff;
    font-size: 14px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 12px;
    text-align: center;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
  .grid-2 {
    grid-template-columns: repeat(2, 1fr);
  }
  .section-label {
    color: #fbbf24;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 1px;
    margin: 14px 0 7px;
  }
  .trigger-hero {
    position: relative;
    overflow: hidden;
    border-radius: 14px;
    padding: 12px;
    margin: 0 0 12px;
    border: 1px solid rgba(251,191,36,0.32);
    background:
      linear-gradient(110deg, rgba(251,191,36,0.18), transparent 38%),
      radial-gradient(circle at 100% 0%, rgba(239,68,68,0.20), transparent 35%),
      #0f172a;
  }
  .trigger-hero::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.08), transparent);
    transform: translateX(-70%);
    animation: triggerSweep 2.8s ease-in-out infinite;
    pointer-events: none;
  }
  .trigger-hero span {
    display: block;
    color: #93c5fd;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.14em;
  }
  .trigger-hero strong {
    display: block;
    color: #fff;
    margin-top: 4px;
    font-size: 18px;
    letter-spacing: -0.02em;
  }
  .trigger-grid {
    gap: 9px;
  }
  .opt {
    background: #374151;
    color: #fff;
    border: 1px solid #4b5563;
    border-radius: 10px;
    padding: 12px 8px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
    font-family: inherit;
    -webkit-tap-highlight-color: transparent;
    text-align: center;
    /* Wrap long player names instead of truncating — two teammates named
       "Sandeep Sh…" must stay distinguishable mid-wicket. Also keeps every
       option at a >=48px touch target. */
    white-space: normal;
    word-break: break-word;
    line-height: 1.2;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  /* Player card option — photo/generated avatar + name + role/bowling line */
  .player-opt {
    justify-content: flex-start;
    gap: 10px;
    text-align: left;
    padding: 8px 10px;
    min-height: 56px;
  }
  .player-opt .pc-img {
    width: 38px; height: 38px; border-radius: 50%;
    object-fit: cover; flex: 0 0 auto;
    border: 2px solid rgba(251,191,36,0.5);
  }
  .player-opt .pc-av {
    width: 38px; height: 38px; border-radius: 50%;
    flex: 0 0 auto;
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 900; color: #fff;
    box-shadow: inset 0 0 0 2px rgba(255,255,255,0.18);
  }
  .player-opt .pc-info {
    display: flex; flex-direction: column; min-width: 0; gap: 1px;
  }
  .player-opt .pc-name {
    font-size: 14px; font-weight: 800; color: #fff;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .player-opt .pc-sub {
    font-size: 10px; font-weight: 700; color: #94a3b8;
    text-transform: uppercase; letter-spacing: 0.3px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .trigger-card {
    min-height: 58px;
    padding: 10px 8px;
    white-space: normal;
    background:
      linear-gradient(160deg, rgba(15,23,42,0.98), rgba(30,41,59,0.96));
    border-color: rgba(148,163,184,0.26);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
  }
  .trigger-card span,
  .trigger-card small {
    display: block;
  }
  .trigger-card span {
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 0.03em;
  }
  .trigger-card small {
    margin-top: 3px;
    color: #94a3b8;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .broadcast-card {
    border-color: rgba(251,191,36,0.28);
  }
  .broadcast-card span {
    color: #fef3c7;
  }
  .scoring-card span {
    color: #bfdbfe;
  }
  .opt:active {
    background: #2563eb;
    transform: scale(0.95);
  }
  @keyframes triggerSweep {
    0%, 55% { transform: translateX(-75%); }
    100% { transform: translateX(75%); }
  }
  .actions {
    margin-top: 12px;
  }
  .btn-cancel {
    width: 100%;
    padding: 10px;
    background: transparent;
    color: #9ca3af;
    border: 1px solid #4b5563;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .btn-cancel:hover {
    color: #fff;
    border-color: #6b7280;
  }
  .muted-text {
    color: #6b7280;
    text-align: center;
    padding: 16px;
    font-size: 13px;
  }
  .muted-sub {
    color: #6b7280;
    font-size: 11px;
    padding: 0 8px 10px;
    text-align: center;
  }
</style>
